/* eslint-disable react/prop-types */
import { ShiftResponseDto, WeekDays } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { ShiftStore } from '@operations/stores/ShiftStore'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShiftMessageHandler } from '../../hooks/useShiftMessageHandler'

interface Props {
  shiftStore: ShiftStore
}

const getFormattedTimeRangeForWeekDay = (shift: ShiftResponseDto, day: WeekDays) => {
  const daysArray = shift.weekDays.split(',').map(day => day.trim())
  if (daysArray.includes(day)) {
    const formatTime = (time: any) => (time as string).split(':').slice(0, 2).join(':')
    return `${formatTime(shift.from)} - ${formatTime(shift.to)}`
  }

  return null
}

export const ShiftTable = observer(({ shiftStore }: Props) => {
  const { t } = useTranslate()
  const { appStore } = useOperationsStore()
  const { getExceptionMessage, getSuccessMessage } = useShiftMessageHandler()
  const navigate = useNavigate()
  const [deleteItems, setDeleteItems] = useState<{ id: number; name: string }[]>([])
  const defaultOptions = useDefaultMRTOptions<ShiftResponseDto>()

  const handleDelete = async () => {
    if (!deleteItems.length) return

    try {
      await shiftStore.deleteShifts(deleteItems.map(item => item.id))
      appStore.setShowAlert('success', getSuccessMessage())
      setDeleteItems([])
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<ShiftResponseDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('shiftName', 'Shift name'),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        header: t('monday', 'Monday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Monday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('tuesday', 'Tuesday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Tuesday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('wednesday', 'Wednesday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Wednesday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('thursday', 'Thursday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Thursday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('friday', 'Friday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Friday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('saturday', 'Saturday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Saturday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        header: t('sunday', 'Sunday'),
        accessorFn: row => getFormattedTimeRangeForWeekDay(row, WeekDays.Sunday),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const tableActions = useMemo<TomTableActionsProps<ShiftResponseDto>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        grouped: true,
        onClick: rows => {
          setDeleteItems(
            rows.map(row => ({
              id: row.original.id,
              name: row.original.name,
            })),
          )
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('operations/shifts')

  const table = useTomTable<ShiftResponseDto>(
    {
      columns,
      data: shiftStore.items,
      enableExpanding: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...(defaultOptions.initialState?.columnPinning?.left ?? []), 'name'],
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-shift-btn`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-shift-btn`,
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='shift-options-btn'
          />
        )
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? `${t('delete', 'Delete')} ${deleteItems[0].name}`
            : t('delete', 'Delete')
        }
        message={t(
          'deletingWillRemoveFromMasterDataPreviousShiftRecordingsWillStay',
          'Deleting {object} will remove it from the master data. Previous recordings with this shift will stay in the system.',
          {
            object:
              deleteItems.length === 1
                ? t('thisItem', 'this item')
                : t('theseItems', 'these items'),
          },
        )}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
